import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isHomepage = location.pathname === '/';

      setScrolled(currentScrollY > 50);

      if (isHomepage) {
        // Hide nav when scrolled past ~90vh, show when scrolling back up
        const heroHeight = window.innerHeight * 0.9;
        if (currentScrollY > heroHeight && currentScrollY > lastScrollY) {
          setHideNav(true);
        } else if (currentScrollY < lastScrollY) {
          setHideNav(false);
        }
      } else {
        setHideNav(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: 'Frameworks', path: '/frameworks' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
  ];

  const isHomepage = location.pathname === '/';
  const showSolid = scrolled || !isHomepage;

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: hideNav ? -100 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          showSolid ? 'bg-dark-bg/95 backdrop-blur-xl border-b border-dark-border' : 'bg-transparent'
        }`}
      >
        {/* Desktop Navigation */}
        <div className="hidden lg:flex max-w-[1400px] mx-auto px-8 py-5 items-center justify-between">
          <Link to="/" className="text-white text-[17px] font-semibold tracking-tight whitespace-nowrap hover:opacity-80 transition-opacity duration-200 cursor-pointer">
            Force Multiplier
          </Link>

          <div className="flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[15px] font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === link.path ? 'text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <Link
              to="/apply"
              className="px-7 py-2.5 bg-accent-blue text-white text-[15px] font-semibold rounded-full hover:brightness-110 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(0,102,255,0.4)] whitespace-nowrap cursor-pointer"
            >
              Apply for Audit
            </Link>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden px-5 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-white text-[18px] font-bold tracking-tight whitespace-nowrap cursor-pointer">
            Force Multiplier
          </Link>

          {/* Hamburger Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-11 h-11 flex items-center justify-center text-white hover:text-accent-blue transition-colors duration-200 cursor-pointer tap-target"
            aria-label="Toggle menu"
          >
            <motion.div
              animate={mobileMenuOpen ? 'open' : 'closed'}
              className="w-6 h-5 flex flex-col justify-between"
            >
              <motion.span
                variants={{
                  closed: { rotate: 0, y: 0 },
                  open: { rotate: 45, y: 8 }
                }}
                transition={{ duration: 0.3 }}
                className="w-full h-0.5 bg-current origin-center"
              />
              <motion.span
                variants={{
                  closed: { opacity: 1 },
                  open: { opacity: 0 }
                }}
                transition={{ duration: 0.2 }}
                className="w-full h-0.5 bg-current"
              />
              <motion.span
                variants={{
                  closed: { rotate: 0, y: 0 },
                  open: { rotate: -45, y: -8 }
                }}
                transition={{ duration: 0.3 }}
                className="w-full h-0.5 bg-current origin-center"
              />
            </motion.div>
          </button>
        </div>

        {/* Scroll progress bar - Desktop only */}
        <motion.div
          style={{ scaleX }}
          className="hidden lg:block absolute bottom-0 left-0 right-0 h-[2px] bg-accent-blue origin-left"
        />
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-0 z-40 bg-dark-bg/98"
            style={{ top: '72px' }}
          >
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="flex flex-col h-full px-5 sm:px-6 py-8 overflow-y-auto"
            >
              {/* Mobile Menu Links */}
              <nav className="flex flex-col gap-1 mb-8">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.15 + index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      className={`block px-4 py-4 text-[15px] font-medium rounded-lg transition-all duration-200 cursor-pointer tap-target min-h-[56px] flex items-center ${
                        location.pathname === link.path
                          ? 'text-white bg-white/5'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* Mobile CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.35 }}
              >
                <Link
                  to="/apply"
                  className="block w-full px-6 py-4 bg-accent-blue text-white text-[16px] font-semibold rounded-full text-center hover:brightness-110 transition-all duration-300 whitespace-nowrap cursor-pointer tap-target min-h-[56px] flex items-center justify-center"
                >
                  Apply for Audit
                </Link>
              </motion.div>

              {/* Mobile Footer Info */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.45 }}
                className="mt-auto pt-8 border-t border-white/10"
              >
                <p className="text-gray-500 text-sm mb-2">Force Multiplier</p>
                <p className="text-gray-600 text-xs leading-relaxed">
                  Increase output without increasing headcount.
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;